import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import apiHelper from "../../../../util/ApiHelper";
import { toast } from "react-toastify";
import editorConfiguration from "../../../../util/EditorConfiguration";
import { useLoading } from "../../../../util/LoadingProvider";
import UploadImage from "../../UploadImage";
import Autocomplete from "@material-ui/lab/Autocomplete";

const schema = yup.object().shape({
  localizations: yup.object().shape({
    en: yup.object().shape({
      name: yup.string(),
      introduction: yup.string(),
    }),
    tc: yup.object().shape({
      name: yup.string(),
      introduction: yup.string(),
    }),
  }),
  status: yup.string(),
  image: yup.string(),
});

const defaultFormValues = {
  localizations: {
    en: {
      name: "",
      position: "",
      introduction: "",
    },
    tc: {
      name: "",
      position: "",
      introduction: "",
    },
  },
  categoryIds:{},
  image: "",
  status: "ACTIVE",
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "40px",
    textAlign: "left",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  input: {
    width: "100%",
  },
}));

export default function JuriesEditPage({ disabled, ...props }) {
  const { match, history } = props;
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const { setLoading } = useLoading();
  const [categoryList, setCategoryList] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);

  const classes = useStyles();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    if (id !== "new") updateJury(value);
    else createJury(value);
  };

  const getJury = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getJuryById(id);
      console.log(result);
      setDefaultValues(result.data);
      setSelectedCats(result.data.categoryIds)
      reset(result.data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id, setLoading]);

  const updateJury = async (value) => {
    try {
      // value.categoryIds = categories;
      console.log(value);
      const result = await apiHelper.hkiff.updateJuryById(id, value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("Your changes are saved.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  const createJury = async (value) => {
    try {
      // value.categoryIds = categories;
      const result = await apiHelper.hkiff.createJury(value);
      console.log(result);
      if (result.status === 200) {
        history.push(`/hkiff/awards/juries/${result.data.id}`);
        toast.dark("Jury is created.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getCategoryList = async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getAwardCategory(
        null,
        0,
        9999999,
        null,
        "",
        true
      );
      console.log("getAwardCategory: ", result);
      setCategoryList(
        result.data.content.map((category) => ({
          id: category.id,
          label: category.localizations.en.name,
        }))
      );
      
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategoryList();
    if (id !== "new") {getJury();}
    
  }, [getJury, id]);

  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          Jury &gt; {id === "new" ? "Add New Jury" : "Edit Jury"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">
          <Grid container xs={12} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Name (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Name (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid container item xs={12}>
              
              <Controller
                defaultValue={[]}
                name="categoryIds"
                control={control}
                render={({
                  field: { onChange, value, ...rest },
                  fieldState: { error },
                }) => {
                  return (
                    <Autocomplete
                      multiple
                      fullWidth
                      label="Categories"
                      options={categoryList}
                      disabled={disabled}
                      getOptionSelected={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Categories"
                          margin="dense"
                          placeholder="Categories"
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                      value={categoryList.filter((o) =>
                        selectedCats?.includes(o.id)
                      )}
                      onChange={(e, values) => {
                        var selected = values.map((item) => item.id);
                        setValue(`categoryIds`, selected);
                        setSelectedCats(selected);
                      }}
                      {...rest}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>

          <Grid
            container
            item
            xs={6}
            direction="row"
            spacing={3}
            style={{ marginTop: "32px" }}
          >
            <Grid item xs={12}>
              {defaultValues && (
                <UploadImage
                  name="image"
                  data={defaultValues}
                  control={control}
                  Controller={Controller}
                  setValue={setValue}
                  getValues={getValues}
                  disabled={disabled}
                  uploadLabel="Image"
                  changeLabel="Image"
                  uploadFolder="JURIES"
                />
              )}
            </Grid>
          </Grid>

          {defaultValues.localizations && (
            <>
              <Box
                fontWeight={900}
                fontSize={13}
                mb={2}
                style={{ marginTop: "30px" }}
                width="100%"
              >
                <Typography variant="p">Description (English)</Typography>
              </Box>

              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.introduction"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value}
                        disabled={disabled}
                        onReady={(editor) => {
                          // You can store the "editor" and use when it is needed.
                          console.log("Editor is ready to use!", editor);
                          editor.setData(
                            defaultValues.localizations.en.introduction
                          );
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                          // console.log({ event, editor, data });
                        }}
                        onBlur={(event, editor) => {
                          console.log("Blur.", editor);
                        }}
                        onFocus={(event, editor) => {
                          console.log("Focus.", editor);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Box fontWeight={900} fontSize={13} mt={4} mb={2} width="100%">
                <Typography variant="p">
                  Description (Traditional Chinese)
                </Typography>
              </Box>

              <Grid container xs={12} direction="row" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.tc.introduction"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value}
                        disabled={disabled}
                        onReady={(editor) => {
                          // You can store the "editor" and use when it is needed.
                          console.log("Editor is ready to use!", editor);
                          editor.setData(
                            defaultValues.localizations.tc.introduction
                          );
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                          // console.log({ event, editor, data });
                        }}
                        onBlur={(event, editor) => {
                          console.log("Blur.", editor);
                        }}
                        onFocus={(event, editor) => {
                          console.log("Focus.", editor);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Grid
            container
            item
            xs={12}
            spacing={3}
            style={{ marginTop: "52px", marginLeft: 0 }}
          >
            <Grid item xs={3} style={{ paddingLeft: "4px" }}>
              <Controller
                control={control}
                name="status"
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <ToggleButtonGroup
                    {...rest}
                    value={value}
                    onChange={(event, values) => {
                      onChange(values);
                    }}
                    exclusive
                    aria-label="Status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value="ACTIVE"
                      aria-label="ACTIVE"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Active
                    </ToggleButton>
                    <ToggleButton
                      value="INACTIVE"
                      aria-label="Inactive"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Inactive
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Grid>
          </Grid>

          

          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
              >
                Save Change
              </Button>
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}
